import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { apiRequest, formatDate, getImageUrl } from "../../../core/common/api";
import MetricCard from "../../../shared/MetricCard";
import PaginationBar from "../../../shared/PaginationBar";
import AppImage from "../../../shared/AppImage";

const DOCUMENT_LABELS: Record<string, string> = {
  gst_certificate: "GST Certificate",
  udyam_certificate: "Udyam Certificate",
  shop_license: "Shop License",
  self_declaration: "Self Declaration",
};

const STATUS_TONE: Record<string, string> = {
  pending: "warning",
  approved: "success",
  rejected: "danger",
};

const KycList = () => {
  const [kycList, setKycList] = useState<any[]>([]);
  const [stats, setStats] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "pending",
    documentType: "all",
  });
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchInput }));
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page),
          limit: String(pagination.limit),
          status: filters.status,
        });
        if (filters.search.trim()) params.set("search", filters.search.trim());
        if (filters.documentType !== "all") {
          params.set("documentType", filters.documentType);
        }

        const [listRes, statsRes] = await Promise.all([
          apiRequest(`/kyc?${params}`, "GET"),
          apiRequest("/kyc/stats", "GET"),
        ]);

        setKycList(listRes.kycList || []);
        setPagination({
          page: listRes.pagination?.page || page,
          limit: listRes.pagination?.limit || pagination.limit,
          totalPages: listRes.pagination?.totalPages || 1,
          total: listRes.pagination?.total || 0,
        });
        setStats(statsRes.stats || {});
      } catch (error: any) {
        Swal.fire("Error", error.message || "Unable to load KYC list", "error");
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit],
  );

  useEffect(() => {
    void loadData(1);
  }, [loadData]);

  return (
    <>
      <div className="page-hdr">
        <div className="page-hdr-left">
          <div className="page-eyebrow">Verification</div>
          <h1 className="page-title">
            KYC <em>Review</em>
          </h1>
          <div className="page-sub">
            Review customer documents and approve or reject KYC.
          </div>
        </div>
      </div>

      <section className="metrics-grid">
        <MetricCard
          metric={{
            label: "Pending",
            value: stats.pending || 0,
            icon: "⏳",
            tone: "orange",
          }}
        />
        <MetricCard
          metric={{
            label: "Approved",
            value: stats.approved || 0,
            icon: "✅",
            tone: "green",
          }}
        />
        <MetricCard
          metric={{
            label: "Rejected",
            value: stats.rejected || 0,
            icon: "❌",
            tone: "purple",
          }}
        />
        <MetricCard
          metric={{
            label: "Not Submitted",
            value: stats.notSubmitted || 0,
            icon: "📄",
            tone: "blue",
          }}
        />
      </section>

      <div className="toolbar">
        <div className="toolbar-search">
          <svg viewBox="0 0 24 24">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, email or phone..."
          />
        </div>

        <select
          className="filter-select"
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="all">All Status</option>
        </select>

        <select
          className="filter-select"
          value={filters.documentType}
          onChange={(e) =>
            setFilters({ ...filters, documentType: e.target.value })
          }
        >
          <option value="all">All Documents</option>
          {Object.entries(DOCUMENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <section className="table-card">
        <div className="table-head">
          <h2>KYC Submissions</h2>
          <p>Open a record to view document and approve or reject</p>
        </div>

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Document</th>
                <th>User</th>
                <th>Type</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    Loading KYC list...
                  </td>
                </tr>
              ) : kycList.length === 0 ? (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No KYC submissions found.
                  </td>
                </tr>
              ) : (
                kycList.map((item) => {
                  const image = getImageUrl(item.documentImage);

                  return (
                    <tr key={item._id}>
                      <td>
                        {image ? (
                          <AppImage
                            src={image}
                            alt="KYC document"
                            className="notification-table-thumb"
                            crossOrigin="anonymous"
                          />
                        ) : (
                          <span className="table-muted">No image</span>
                        )}
                      </td>
                      <td>
                        <strong>{item.user?.name || "User"}</strong>
                        <small className="table-muted">
                          {item.user?.phone || item.user?.email || "-"}
                        </small>
                      </td>
                      <td>
                        <span className="status-badge muted">
                          {DOCUMENT_LABELS[item.documentType] ||
                            item.documentType ||
                            "—"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`status-badge ${STATUS_TONE[item.status] || "muted"}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td>{formatDate(item.submittedAt || item.createdAt)}</td>
                      <td>
                        <Link
                          to={`/kyc/${item._id}`}
                          className="btn-sm btn-outline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <PaginationBar
          page={pagination.page}
          limit={pagination.limit}
          total={pagination.total}
          totalPages={pagination.totalPages}
          itemLabel="submissions"
          onPageChange={loadData}
          onLimitChange={(limit) =>
            setPagination((current) => ({ ...current, page: 1, limit }))
          }
        />
      </section>
    </>
  );
};

export default KycList;
